// Imports
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';
import { useAppDispatch } from '../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../shareables/integrations/api/redux/actions';
import { GetNewsArticlesResponse } from '../../../../../../shareables/types/api/main/website/page/type/204/news-articles/get';
import parseAndSanitizeMarkdown from '../../../../../../shareables/utils/markdown/parse-and-sanitize';
import { StyledEditorGeneratedContentContainer } from '../shared-components/styled-editor-generated-content-container';
import Attachments from '../shared-components/attachments';
import Tippy from '@tippyjs/react';
import tippyProps from '../../../../../../shareables/foundation/front-end/utils/tippy-props';


// Styled components
const Container = styled.div`
	margin-top: -0.0938rem;
`;

const NewsArticle = styled.article`
	padding-top: 0.0938rem;
	hr {
		margin-top: 1.25rem;
		margin-bottom: 1.25rem;
		border: 0;
		border-top: 0.0625rem solid #eee;
	}
`;

export const NewsArticleTitle = styled.h3`
	font-size: 1.5rem;
	font-weight: 500;
	line-height: 1.1;
	margin-bottom: 0.625rem;
	overflow-wrap: anywhere;
	
	a {
		color: inherit;
		text-decoration: none;
		
		&:hover,
		&:focus {
			color: inherit;
		}
	}
	
	small {
		font-style: italic;
		font-size: 65%;
		margin-left: 0.4531rem;
	}
`;

export const NewsArticleContent = styled(StyledEditorGeneratedContentContainer)`
	width: 100%;
	overflow-wrap: anywhere;
	
	p {
		font-size: inherit;
		line-height: 1.3125rem;
	}
`;

const ReadFullArticle = styled.div`
	margin-top: 0.5rem;
	margin-bottom: 0.5rem;
	
	svg {
		margin-right: 0.3125rem;
	}
`;

const AttachmentsWrapper = styled.div`
	margin-top: 0.75rem;
`;


// Define the accepted props
interface Props {
	pageID: number;
	storybookNewsArticles?: GetNewsArticlesResponse;
}


// Function component
const NewsArticles: React.FC<Props> = ({ pageID, storybookNewsArticles }) => {
	// Use state
	const [newsArticles, setNewsArticles] = useState<GetNewsArticlesResponse | null>(storybookNewsArticles ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load news articles on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetNewsArticlesResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/page/id_${pageID}/type/204/news-articles`,
				completion: (json) => {
					setNewsArticles(json.response);
				},
			})
		);
	}, [dispatch, pageID]);
	
	
	// If loading, display nothing
	if (newsArticles === null) {
		return null;
	}
	
	
	// Return JSX
	return (
		<Container>
			{newsArticles.map((newsArticle, i) => {
				// Parse and sanitize news article content
				const newsArticleText = parseAndSanitizeMarkdown(newsArticle.text);
				
				
				// Initialize
				const parser = new DOMParser();
				const newsArticleDocument = parser.parseFromString(newsArticleText, 'text/html');
				
				
				// Get the first two child elements from the body
				const childrenElements = Array.from(newsArticleDocument.body.children);
				const firstTwoChildrenElements = childrenElements.slice(0, 2);
				
				
				// Convert to string
				const firstTwoChildrenHTMLString = firstTwoChildrenElements.map((child) => child.outerHTML).join('');
				
				
				// Determine if there are more than two children
				const hasMoreContent = childrenElements.length > 2;
				
				
				// Initialize
				const sanitizedContent = {
					__html: firstTwoChildrenHTMLString,
				};
				
				const newsArticleLink = `article/${window.encodeURIComponent(
					newsArticle.title.replace(/\W+/g, ' ').replace(/ /g, '-')
				)}-${newsArticle.id}`;
				
				return (
					<NewsArticle key={newsArticle.id}>
						<NewsArticleTitle>
							<Link to={newsArticleLink} state={{ disableScrollToTop: true }}>
								{newsArticle.title}
							</Link>
							<small className='is-gray'>
								Published{' '}
								<Tippy
									{...tippyProps}
									content={new Date(newsArticle.published).toLocaleDateString(undefined, {
										month: 'long',
										day: 'numeric',
										year: 'numeric',
										hour: 'numeric',
										minute: '2-digit',
									})}
								>
									<span>{DateTime.fromISO(newsArticle.published).toRelative()}</span>
								</Tippy>
							</small>
						</NewsArticleTitle>
						
						<NewsArticleContent dangerouslySetInnerHTML={sanitizedContent} />
						
						{hasMoreContent && (
							<React.Fragment>
								<p>…</p>
								<ReadFullArticle>
									<Link to={newsArticleLink} state={{ disableScrollToTop: true }}>
										<FontAwesomeIcon icon={faAngleRight} />
										Read full article
									</Link>
								</ReadFullArticle>
							</React.Fragment>
						)}
						
						{newsArticle.attachments.length > 0 && (
							<AttachmentsWrapper>
								<Attachments
									id={newsArticle.id}
									attachmentURIPrefix='article'
									attachments={newsArticle.attachments.map((attachment) => ({ ...attachment, name: attachment.title }))}
								/>
							</AttachmentsWrapper>
						)}
						
						{i < newsArticles.length - 1 && <hr />}
					</NewsArticle>
				);
			})}
			
			{newsArticles.length === 0 && <em>No news articles have been published yet…</em>}
		</Container>
	);
};

export default NewsArticles;
